import React, { useState } from 'react';
import Markdown from 'react-markdown';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import remarkGfm from 'remark-gfm';

import { useQuery } from '@tanstack/react-query';

import { Alert, Case, CaseDetails, useAlertsApi, useCasesApi } from '../../api';
import Button from '../../components/Library/Button';
import Frame from '../../components/Library/Frame';
import { Modal, ModalBody, ModalHeader } from '../../components/Library/Modal';
import CenterLoadingSpinner from '../../components/LoadingSpinner/CenterLoadingSpinner';
import { formatToLocalTime } from '../../libs/libDate';
import { titleCase } from '../../libs/libString';
import AlertActivityDisplay from './AlertActivity';

interface Detail {
  label: string;
  getValue: (alert: Alert) => React.ReactNode;
}

const DETAILS: Detail[] = [
  {
    label: 'Alert Time',
    getValue: (a) => a.alert_time == null ? <i>Null</i> : formatToLocalTime(a.alert_time)
  },
  {
    label: 'Source',
    getValue: (a) => a.alert_source ?? <i>Null</i>
  },
  {
    label: 'Status',
    getValue: (a) => a.status
  },
  {
    label: 'External Status',
    getValue: (a) => a.external_alert_status
  }
];

interface CaseDetail {
  label: string;
  getValue: (alert: Case) => React.ReactNode;
}

const CASE_DETAILS: CaseDetail[] = [
  {
    label: 'Subject',
    getValue: (a) => a.title
  },
  {
    label: 'Status',
    getValue: (a) => titleCase(a.status)
  }
];

const AlertDetailsBody: React.FC = () => {
  const { alertId } = useParams();
  const [queryParams] = useSearchParams();
  const alertsApi = useAlertsApi();
  const casesApi = useCasesApi();
  const navigate = useNavigate();

  const [detailExpanded, setDetailExpanded] = useState(false);

  const alertDetails = useQuery(['alert', alertId], async () => {
    const alert = await alertsApi.get_alert_by_id({
      params: {
        alert_id: alertId ?? ''
      }
    });

    return alert;
  });

  const caseQuery = useQuery(['cases', alertDetails.data?.related_case_id], async () => {
    if (alertDetails.data == null || alertDetails.data.related_case_id == null) {
      return null;
    }
    const caseInfo = await casesApi.get_case_by_id({
      params: {
        case_id: alertDetails.data?.related_case_id
      }
    });

    return caseInfo;
  });

  const activityQuery = useQuery(['alert', alertId, 'activity'], async () => {
    const activity = await alertsApi.get_alert_activity_by_alert_id({
      params: {
        alert_id: alertId ?? '',
      },
      queries: {

      }
    });

    return activity;
  });

  const closeAlert = (): void => {
    navigate(`..${decodeURIComponent(queryParams.get('alertsQuery') ?? '')}`);
  }

  if (alertDetails.isError) {
    return <>
      <ModalHeader closeModal={closeAlert}>Alert Loading Error</ModalHeader>
      <ModalBody>
        
      </ModalBody>
    </>
  }

  if (alertDetails.data == null) {
    return <>
      <ModalHeader closeModal={closeAlert}>Loading...</ModalHeader>
      <ModalBody>
        <CenterLoadingSpinner />
      </ModalBody>
    </>
  }

  const alert = alertDetails.data;

  return <>
    <ModalHeader closeModal={closeAlert}>{alert.name}</ModalHeader>
    <ModalBody className='overflow-y-auto'>
      <div className='lg:flex w-full'>
        <div className='lg:w-96 pb-2'>
          <Frame varient='nav' title={<span className='text-xl font-bold'>Attributes</span>}>
            <table className='w-full'>
              <tbody>
                {DETAILS.map((detail) => (
                  <tr key={detail.label}>
                    <td className='font-bold py-1 pr-1'>{detail.label}</td>
                    <td>{detail.getValue(alert)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className='w-full flex'>
            {alert.external_alert_url != null && <a href={alert.external_alert_url} target='_blank' className='mx-auto my-1 px-2 py-1 rounded-lg text-primary-text bg-primary hover:bg-primary-dark shadow-md shadow-hover'>Open Alert in Originating Platform</a>}
            </div>
          </Frame>
          {alert.related_case_id != null &&
          <Frame varient='nav' title={<span className='text-xl font-bold'>Related Case</span>}>
                {caseQuery.data == null ? <CenterLoadingSpinner /> : <>
                    {CASE_DETAILS.map((detail) => (
                      <tr key={detail.label}>
                        <td className='font-bold py-1 pr-1'>{detail.label}</td>
                        <td>{detail.getValue(caseQuery.data!)}</td>
                      </tr>
                    ))}
                    <div className='w-full flex'>
                      <Link to={`/cases/${caseQuery.data!.id}`} className='mx-auto my-1 px-2 py-1 rounded-lg text-primary-text bg-primary hover:bg-primary-dark shadow-md shadow-hover'>Open Case</Link>
                    </div>
                </>}
          </Frame>}
        </div>
        <div className='lg:flex-1 max-w-[100vw] lg:max-w-[calc(100vw-544px)] max-h-full overflow-y-auto'>
          <Frame varient='nav' title={<span className='text-xl font-bold'>Alert Detail</span>}>
                {detailExpanded ? 
                <>
                  <Markdown remarkPlugins={[remarkGfm]}>{alert.detail}</Markdown>
                  <a onClick={() => setDetailExpanded(false)}><i className="bi bi-caret-up-square cursor-pointer"></i> <span className='underline'>Collapse Details</span></a>
                </> 
                : 
                <><a onClick={() => setDetailExpanded(true)} className='cursor-pointer'><i className="bi bi-caret-down-square"></i> <span className='underline'>Expand Details</span></a></>}
                
          </Frame>
          <Frame varient='nav' title={<span className='text-xl font-bold'>Activities</span>}>
                {activityQuery.data == null ? 
                <CenterLoadingSpinner /> :
                <>
                  {activityQuery.data.map((activity) => (
                    <AlertActivityDisplay alertId={alert.id} activity={activity} />
                  ))}
                  <div className='w-full flex'>
                    <div className='p-2 mx-auto'>
                      {activityQuery.data.length === 0 && (
                        caseQuery.data == null ? 
                          <>There is no activity for this alert.</>
                        : <>Please visit the <Link to={`/cases/${caseQuery.data!.id}`} className='underline'>case</Link> to view alert activitiy.</>
                      )}
                    </div>
                  </div>
                </>}
          </Frame>
        </div>
      </div>
    </ModalBody>
  </>;
}

const AlertDetails: React.FC = () => {
  const navigate = useNavigate();
  const [queryParams] = useSearchParams();

  const closeAlert = () => {
    navigate(`..${decodeURIComponent(queryParams.get('alertsQuery') ?? '')}`);
  }

  return <>
    <Modal show={true} closeModal={closeAlert} modalSize='Screen'>
      <AlertDetailsBody />
    </Modal>
  </>
};

export default AlertDetails;